import {storeToRefs} from 'pinia';
import {useDataTracking, useRoute} from '#imports';
import {useAdvancedSearchStore} from '~/features/advanced-search/stores/advancedSearch.store';
import type {FieldEditionSource} from '~/features/advanced-search/types';
import {AdvancedSearchTrackingEvent} from './events';
import type {StepName} from './tracking.utils';
import {resolveStepFromRoute} from './tracking.utils';

export function useAdvancedSearchTracker() {
    const {track} = useDataTracking();
    const route = useRoute();
    const {advancedSearch} = storeToRefs(useAdvancedSearchStore());

    const payloadBase = () => ({
        advanced_search_id: advancedSearch.value?.id,
        brief_id: route.params.briefId?.toString(),
        ...(route.query.brief_origin ? {entry_point: route.query.brief_origin} : undefined),
    });

    const emit = (event: string, payload?: Record<string, unknown>, options?: {includeContext?: boolean}) =>
        track(event, options?.includeContext === false ? (payload ?? {}) : {...payloadBase(), ...(payload ?? {})});

    const trackFunnelStepViewed = (stepName?: StepName) =>
        emit(AdvancedSearchTrackingEvent.FunnelStepViewed, {step_name: stepName ?? resolveStepFromRoute(route)});

    const trackSingleEdit = (p: {operation: string; updated_criteria: string; stepName?: StepName; source?: FieldEditionSource}) =>
        emit(AdvancedSearchTrackingEvent.Edited, {
            operations: p.operation,
            updated_criteria: p.updated_criteria,
            step_name: p.stepName ?? resolveStepFromRoute(route),
            source: p.source,
        });

    const trackExit = (stepName?: StepName) => emit(AdvancedSearchTrackingEvent.Exit, {step_name: stepName ?? resolveStepFromRoute(route)});

    const trackAutocontactOptIn = (optIn: boolean) => emit(AdvancedSearchTrackingEvent.AutocontactOptedIn, {opt_in: optIn});

    const trackCalibrationOptIn = ({optIn}: {optIn: boolean}) => emit(AdvancedSearchTrackingEvent.CalibrationOptedIn, {opt_in: optIn});

    const trackFreelancerShortlisted = (profiles: any[], advancedSearchQueryId?: string) =>
        emit(AdvancedSearchTrackingEvent.Shortlisted, {advanced_search_query_id: advancedSearchQueryId, profiles});

    const trackFreelancerRejected = (p: {
        freelancerProfileId: string;
        headline: string;
        dailyRate?: string;
        areSkillsReordered?: boolean;
        stepName?: StepName;
        advancedSearchQueryId?: string;
    }) =>
        emit(AdvancedSearchTrackingEvent.Rejected, {
            step_name: p.stepName ?? resolveStepFromRoute(route),
            freelancer_profile_id: p.freelancerProfileId,
            headline: p.headline,
            daily_rate: p.dailyRate,
            are_skills_reordered: p.areSkillsReordered,
            advanced_search_query_id: p.advancedSearchQueryId,
        });

    const trackAssistanceRequested = (stepName?: StepName) =>
        emit(AdvancedSearchTrackingEvent.AssistanceRequested, {step_name: stepName ?? resolveStepFromRoute(route)});

    const trackSelectionSkipped = (stepName?: StepName) =>
        emit(AdvancedSearchTrackingEvent.SelectionSkipped, {step_name: stepName ?? resolveStepFromRoute(route)});

    const trackEmptyState = (advancedSearchQueryId?: string) =>
        emit(AdvancedSearchTrackingEvent.EmptyState, {advanced_search_query_id: advancedSearchQueryId});

    const trackCatalogPromptSelected = (catalogId: string) =>
        emit(AdvancedSearchTrackingEvent.CatalogPromptSelected, {catalog_id: catalogId}, {includeContext: false});

    const trackProfileViewed = (profileId: string, advancedSearchQueryId?: string) =>
        emit(AdvancedSearchTrackingEvent.ProfileViewed, {
            advanced_search_query_id: advancedSearchQueryId,
            profile_id: profileId,
            origin: 'AI_SEARCH_DRAWER',
            mode: 'read_only',
        });

    const trackDisplayFullProfile = (profileId: string) => emit(AdvancedSearchTrackingEvent.DisplayFullProfile, {profile_id: profileId});

    const trackSkillFreeTextEntered = (skills: string[], stepName?: StepName) =>
        emit(AdvancedSearchTrackingEvent.SkillFreeTextEntered, {skills, step_name: stepName ?? resolveStepFromRoute(route)});

    const trackDraftResume = (stepName?: StepName) =>
        emit(AdvancedSearchTrackingEvent.DraftResume, {step_name: stepName ?? resolveStepFromRoute(route)});

    const trackSeeMoreProfiles = () => emit(AdvancedSearchTrackingEvent.SeeMoreProfiles);

    const trackSubmitAdvancedSearchDisplayExplanation = () =>
        emit(
            AdvancedSearchTrackingEvent.SubmitAdvancedSearchDisplayExplanation,
            {
                advanced_search_id: advancedSearch.value?.id,
                brief_id: advancedSearch.value?.briefId,
            },
            {includeContext: false},
        );

    const trackProjectDescriptionGenerationRequested = (p: {attempt: number}) =>
        emit(AdvancedSearchTrackingEvent.ProjectDescriptionGenerationRequested, {attempt: p.attempt});

    const trackProjectDescriptionGenerationUndone = () => emit(AdvancedSearchTrackingEvent.ProjectDescriptionGenerationUndone);

    return {
        trackFunnelStepViewed,
        trackSingleEdit,
        trackExit,
        trackAutocontactOptIn,
        trackCalibrationOptIn,
        trackFreelancerShortlisted,
        trackFreelancerRejected,
        trackAssistanceRequested,
        trackSelectionSkipped,
        trackEmptyState,
        trackCatalogPromptSelected,
        trackProfileViewed,
        trackDisplayFullProfile,
        trackSkillFreeTextEntered,
        trackDraftResume,
        trackSeeMoreProfiles,
        trackSubmitAdvancedSearchDisplayExplanation,
        trackProjectDescriptionGenerationRequested,
        trackProjectDescriptionGenerationUndone,
    };
}
